import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Camera, Loader2, PenLine, CheckCircle2, ScanLine } from "lucide-react";
import RetroInvoiceAmounts, { retroLinesFrom } from "@/components/RetroInvoiceAmounts";

const CURRENCIES = ["MAD", "EUR", "USD", "GBP", "CHF", "CAD"];
const MODES = ["VIREMENT", "CHEQUE", "ESPECES", "CARTE", "PRELEVEMENT", "AUTRE"];

const emptyForm = {
  number: "", client_id: "", client_name: "", mission_id: "", mission_title: "",
  invoice_date: "", due_date: "", year: "", month: "", currency: "MAD",
  total_ht: "", total_tva: "", total_ttc: "", description: "",
  lines: [{ description: "", amount_ht: "" }], vat_rate: "20",
  paid: false, paid_date: "", paid_amount: "", paid_amount_mad: "",
  payment_mode: "VIREMENT", payment_reference: "", bank_account: "", payment_comment: "",
};

export const RetroInvoiceBlock = ({ onCreated, editInvoice, onEditClose }) => {
  const fileRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null); // Iter-259 — edit mode
  const [stagedFileId, setStagedFileId] = useState(null);
  const [confidence, setConfidence] = useState(null);
  const [saving, setSaving] = useState(false);
  const [scanProgress, setScanProgress] = useState(null);
  // Iter-248 — multi-scan validation queue + live duplicate check.
  const [queue, setQueue] = useState([]);
  const [queueIndex, setQueueIndex] = useState(0);
  const [dupInfo, setDupInfo] = useState(null);
  const [clients, setClients] = useState([]);
  const [missions, setMissions] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);

  useEffect(() => {
    if (!dialogOpen) return;
    (async () => {
      try {
        const [c, m] = await Promise.all([api.get("/clients"), api.get("/missions")]);
        setClients(Array.isArray(c.data) ? c.data : c.data?.clients || []);
        setMissions(Array.isArray(m.data) ? m.data : m.data?.missions || []);
      } catch { /* selects stay empty, free text still works */ }
      try {
        const b = await api.get("/admin/bank-accounts");
        setBankAccounts(b.data?.accounts || b.data?.rows || (Array.isArray(b.data) ? b.data : []));
      } catch { /* optional */ }
    })();
  }, [dialogOpen]);

  const openManual = () => {
    setForm(emptyForm);
    setEditingId(null);
    setStagedFileId(null);
    setConfidence(null);
    setQueue([]);
    setQueueIndex(0);
    setDupInfo(null);
    setDialogOpen(true);
  };

  // Iter-259 — open the dialog pre-filled to edit an existing retro invoice.
  useEffect(() => {
    if (!editInvoice) return;
    const pd = editInvoice.payment_details || {};
    setForm({
      number: editInvoice.number || "",
      client_id: editInvoice.client_id || "",
      client_name: editInvoice.client_name || "",
      mission_id: editInvoice.mission_id || "",
      mission_title: editInvoice.mission_title || "",
      invoice_date: String(editInvoice.sent_at || "").slice(0, 10),
      due_date: String(editInvoice.due_date || "").slice(0, 10),
      year: editInvoice.year != null ? String(editInvoice.year) : "",
      month: editInvoice.month != null ? String(editInvoice.month) : "",
      currency: (editInvoice.currency || "MAD").toUpperCase(),
      total_ht: editInvoice.total_ht != null ? String(editInvoice.total_ht) : "",
      total_tva: editInvoice.total_tva != null ? String(editInvoice.total_tva) : "",
      total_ttc: editInvoice.total_ttc != null ? String(editInvoice.total_ttc) : "",
      description: editInvoice.description || "",
      ...retroLinesFrom(editInvoice),
      paid: !!editInvoice.paid_at,
      paid_date: String(editInvoice.paid_at || "").slice(0, 10),
      paid_amount: editInvoice.paid_amount != null ? String(editInvoice.paid_amount) : "",
      paid_amount_mad: editInvoice.paid_amount_mad != null ? String(editInvoice.paid_amount_mad) : "",
      payment_mode: pd.payment_mode || "VIREMENT",
      payment_reference: pd.payment_reference || "",
      bank_account: pd.bank_account || "",
      payment_comment: pd.comment || "",
    });
    setEditingId(editInvoice.invoice_id);
    setStagedFileId(null);
    setConfidence(null);
    setQueue([]);
    setQueueIndex(0);
    setDupInfo(null);
    setDialogOpen(true);
  }, [editInvoice]);

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
    onEditClose?.();
  };

  const checkDuplicate = async (num) => {
    const n = (num || "").trim();
    if (!n) { setDupInfo(null); return; }
    try {
      const r = await api.get(`/invoices/check-number?number=${encodeURIComponent(n)}`);
      const dup = r.data?.exists ? (r.data.invoice || {}) : null;
      // Iter-259 — in edit mode, the invoice's own number is not a duplicate.
      setDupInfo(dup && dup.invoice_id !== editingId ? dup : null);
    } catch { setDupInfo(null); }
  };

  const applyDraft = (draft) => {
    const x = draft.extracted || {};
    setForm({
      ...emptyForm,
      number: x.invoice_number || "",
      client_name: x.client_name || "",
      invoice_date: x.invoice_date || "",
      due_date: x.due_date || "",
      currency: (x.currency || "MAD").toUpperCase(),
      total_ht: x.total_ht != null ? String(x.total_ht) : "",
      total_tva: x.total_tva != null ? String(x.total_tva) : "",
      total_ttc: x.total_ttc != null ? String(x.total_ttc) : "",
      description: x.description || "",
      ...retroLinesFrom(x),
    });
    setStagedFileId(draft.staged_file_id || null);
    setConfidence(typeof x.confidence === "number" ? x.confidence : null);
    setDupInfo(null);
    if (x.invoice_number) checkDuplicate(x.invoice_number);
  };

  const advanceQueue = (q, idx) => {
    if (idx < q.length) {
      setQueueIndex(idx);
      applyDraft(q[idx]);
    } else {
      setDialogOpen(false);
      if (q.length > 1) toast.success("File de scans terminée");
    }
  };

  const skipCurrent = () => advanceQueue(queue, queueIndex + 1);

  const onFilePicked = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;
    const tooBig = files.find((f) => f.size > 10 * 1024 * 1024);
    if (tooBig) { toast.error(`${tooBig.name} : trop volumineux (max 10 Mo)`); return; }
    setScanning(true);
    const drafts = [];
    try {
      for (let i = 0; i < files.length; i++) {
        setScanProgress({ current: i + 1, total: files.length, name: files[i].name });
        const fd = new FormData();
        fd.append("file", files[i]);
        try {
          const r = await api.post("/invoices/scan", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
          drafts.push({ staged_file_id: r.data?.staged_file_id, extracted: r.data?.extracted || {}, filename: files[i].name });
        } catch (err) {
          toast.error(`${files[i].name} : ${err?.response?.data?.detail || "analyse échouée"}`);
        }
      }
      if (drafts.length === 0) return;
      setQueue(drafts);
      setQueueIndex(0);
      applyDraft(drafts[0]);
      setDialogOpen(true);
      toast.success(drafts.length > 1
        ? `${drafts.length} factures analysées — validez-les une par une`
        : "Facture analysée — vérifiez et complétez les informations");
    } finally {
      setScanning(false);
      setScanProgress(null);
    }
  };

  const submit = async () => {
    if (!form.number.trim()) { toast.error("N° de facture requis"); return; }
    if (dupInfo) { toast.error(`Refusé : la facture ${form.number.trim()} existe déjà`); return; }
    if (!form.client_id && !form.client_name.trim()) { toast.error("Client requis"); return; }
    if (!form.invoice_date) { toast.error("Date d'émission requise"); return; }
    const ttc = parseFloat(form.total_ttc);
    if (!Number.isFinite(ttc) || ttc <= 0) { toast.error("Montant TTC invalide"); return; }
    if (form.paid && !form.paid_date) { toast.error("Date de paiement requise"); return; }
    setSaving(true);
    try {
      const payload = {
        number: form.number.trim(),
        client_id: form.client_id || null,
        client_name: form.client_name.trim() || null,
        mission_id: form.mission_id || null,
        mission_title: form.mission_title.trim() || null,
        invoice_date: form.invoice_date,
        due_date: form.due_date || null,
        year: form.year ? parseInt(form.year, 10) : null,
        month: form.month ? parseInt(form.month, 10) : null,
        currency: form.currency,
        total_ht: form.total_ht ? parseFloat(form.total_ht) : null,
        total_tva: form.total_tva ? parseFloat(form.total_tva) : null,
        total_ttc: ttc,
        description: (form.lines[0]?.description || form.description || "").trim() || null,
        lines: form.lines.filter((l) => l.description.trim() || l.amount_ht !== "")
          .map((l, i) => ({ description: l.description.trim() || `Ligne ${i + 1}`, amount_ht: parseFloat(l.amount_ht) || 0 })),
        vat_rate: parseFloat(form.vat_rate),
        paid: form.paid,
        paid_date: form.paid ? form.paid_date : null,
        paid_amount: form.paid && form.paid_amount ? parseFloat(form.paid_amount) : null,
        paid_amount_mad: form.paid && form.paid_amount_mad ? parseFloat(form.paid_amount_mad) : null,
        payment_mode: form.paid ? form.payment_mode : null,
        payment_reference: form.paid ? form.payment_reference.trim() || null : null,
        bank_account: form.paid ? form.bank_account || null : null,
        payment_comment: form.paid ? form.payment_comment.trim() || null : null,
        staged_file_id: stagedFileId,
      };
      const r = editingId
        ? await api.put(`/invoices/retro/${editingId}`, payload)
        : await api.post("/invoices/retro", payload);
      toast.success(editingId
        ? `Facture ${r.data.number} mise à jour${r.data.paid ? " (payée)" : ""}`
        : `Facture ${r.data.number} créée${r.data.paid ? " (payée)" : ""}`);
      onCreated?.();
      if (editingId) {
        closeDialog();
      } else if (queue.length > 0 && queueIndex + 1 < queue.length) {
        advanceQueue(queue, queueIndex + 1);
      } else {
        setDialogOpen(false);
        if (queue.length > 1) toast.success("File de scans terminée");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || (editingId ? "Mise à jour échouée" : "Création échouée"));
    } finally { setSaving(false); }
  };

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const clientMissions = missions.filter((m) => !form.client_id || m.client_id === form.client_id);

  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-4" data-testid="retro-invoice-block">
      <div className="flex items-center gap-2 mb-1">
        <ScanLine size={15} className="text-zinc-500" />
        <h2 className="text-sm font-semibold text-zinc-900">Factures rétroactives</h2>
      </div>
      <p className="text-xs text-zinc-500 mb-3">
        Intégrez une facture émise hors application : scan + reconnaissance IA ou saisie manuelle.
        Elle sera comptabilisée dans l'État mensuel et la Marge réelle au mois de son paiement.
      </p>
      <div className="flex flex-wrap gap-2">
        <input ref={fileRef} type="file" multiple accept="image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf" className="hidden" onChange={onFilePicked} data-testid="retro-scan-input" />
        <button
          onClick={() => fileRef.current?.click()}
          disabled={scanning}
          data-testid="retro-scan-btn"
          className="inline-flex items-center gap-2 px-4 h-10 border-2 border-dashed border-zinc-300 text-sm text-zinc-700 rounded-md hover:border-zinc-500 hover:bg-zinc-50 disabled:opacity-60"
        >
          {scanning ? <Loader2 size={15} className="animate-spin" /> : <Camera size={15} />}
          {scanning
            ? (scanProgress ? `Analyse IA ${scanProgress.current}/${scanProgress.total}…` : "Analyse IA en cours…")
            : "Scanner une ou plusieurs factures (IA)"}
        </button>
        <button
          onClick={openManual}
          data-testid="retro-manual-btn"
          className="inline-flex items-center gap-2 px-4 h-10 border border-zinc-200 text-sm text-zinc-700 rounded-md hover:bg-zinc-50"
        >
          <PenLine size={15} /> Saisie manuelle
        </button>
      </div>

      <Dialog open={dialogOpen} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent data-testid="retro-invoice-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Facture rétroactive — validation
              {queue.length > 1 && (
                <span className="ml-2 text-xs font-mono font-normal text-zinc-500" data-testid="retro-queue-position">
                  ({queueIndex + 1}/{queue.length}{queue[queueIndex]?.filename ? ` — ${queue[queueIndex].filename}` : ""})
                </span>
              )}
            </DialogTitle>
            <DialogDescription>
              Vérifiez les informations extraites{confidence != null ? ` (confiance IA : ${Math.round(confidence * 100)}%)` : ""} et complétez les champs manquants.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>N° facture <span className="text-red-600">*</span></Label>
                <Input
                  value={form.number}
                  onChange={(e) => { set({ number: e.target.value }); setDupInfo(null); }}
                  onBlur={(e) => checkDuplicate(e.target.value)}
                  data-testid="retro-number" placeholder="FA-2025-012"
                  className={dupInfo ? "border-red-400" : ""}
                />
                {dupInfo && (
                  <p className="mt-1 text-[11px] text-red-600 font-medium" data-testid="retro-duplicate-warning">
                    ⚠ Cette facture existe déjà{dupInfo.client_name ? ` (${dupInfo.client_name})` : ""} — ajout refusé.
                  </p>
                )}
              </div>
              <div>
                <Label>Devise</Label>
                <select value={form.currency} onChange={(e) => set({ currency: e.target.value })} data-testid="retro-currency" className="w-full h-10 px-2 text-sm border border-zinc-200 rounded-md bg-white">
                  {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Client <span className="text-red-600">*</span></Label>
                <select
                  value={form.client_id}
                  onChange={(e) => {
                    const cid = e.target.value;
                    const cli = clients.find((c) => c.client_id === cid);
                    set({ client_id: cid, client_name: cli ? (cli.name || "") : form.client_name, mission_id: "" });
                  }}
                  data-testid="retro-client-select"
                  className="w-full h-10 px-2 text-sm border border-zinc-200 rounded-md bg-white"
                >
                  <option value="">— Saisie libre —</option>
                  {clients.map((c) => <option key={c.client_id} value={c.client_id}>{c.name}</option>)}
                </select>
                {!form.client_id && (
                  <Input className="mt-1" value={form.client_name} onChange={(e) => set({ client_name: e.target.value })} data-testid="retro-client-name" placeholder="Nom du client" />
                )}
              </div>
              <div>
                <Label>Mission (optionnel)</Label>
                <select value={form.mission_id} onChange={(e) => set({ mission_id: e.target.value })} data-testid="retro-mission-select" className="w-full h-10 px-2 text-sm border border-zinc-200 rounded-md bg-white">
                  <option value="">—</option>
                  {clientMissions.map((m) => <option key={m.mission_id} value={m.mission_id}>{m.title}</option>)}
                </select>
                {!form.mission_id && (
                  <Input className="mt-1" value={form.mission_title} onChange={(e) => set({ mission_title: e.target.value })} data-testid="retro-mission-title" placeholder="Intitulé mission (libre)" />
                )}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Date émission <span className="text-red-600">*</span></Label>
                <Input type="date" value={form.invoice_date} onChange={(e) => set({ invoice_date: e.target.value })} data-testid="retro-invoice-date" />
              </div>
              <div>
                <Label>Échéance</Label>
                <Input type="date" value={form.due_date} onChange={(e) => set({ due_date: e.target.value })} data-testid="retro-due-date" />
              </div>
              <div>
                <Label>Période facturée</Label>
                <div className="flex gap-1">
                  <Input type="number" min="1" max="12" placeholder="MM" value={form.month} onChange={(e) => set({ month: e.target.value })} data-testid="retro-month" />
                  <Input type="number" min="2000" max="2999" placeholder="AAAA" value={form.year} onChange={(e) => set({ year: e.target.value })} data-testid="retro-year" />
                </div>
              </div>
            </div>
            <RetroInvoiceAmounts form={form} set={set} />
            <label className="inline-flex items-center gap-2 text-sm cursor-pointer pt-1">
              <input type="checkbox" checked={form.paid} onChange={(e) => set({ paid: e.target.checked })} data-testid="retro-paid-toggle" className="h-3.5 w-3.5" />
              <span className="font-medium">Facture déjà payée (règlement encaissé)</span>
            </label>
            {form.paid && (
              <div className="border border-emerald-200 bg-emerald-50/40 rounded-md p-3 space-y-3" data-testid="retro-payment-block">
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label>Date paiement <span className="text-red-600">*</span></Label>
                    <Input type="date" value={form.paid_date} onChange={(e) => set({ paid_date: e.target.value })} data-testid="retro-paid-date" />
                  </div>
                  <div>
                    <Label>Montant payé ({form.currency})</Label>
                    <Input type="number" step="0.01" value={form.paid_amount} onChange={(e) => set({ paid_amount: e.target.value })} data-testid="retro-paid-amount" placeholder="= TTC par défaut" />
                  </div>
                  {form.currency !== "MAD" && (
                    <div>
                      <Label>Reçu en MAD</Label>
                      <Input type="number" step="0.01" value={form.paid_amount_mad} onChange={(e) => set({ paid_amount_mad: e.target.value })} data-testid="retro-paid-amount-mad" placeholder="contre-valeur" />
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label>Mode</Label>
                    <select value={form.payment_mode} onChange={(e) => set({ payment_mode: e.target.value })} data-testid="retro-payment-mode" className="w-full h-10 px-2 text-sm border border-zinc-200 rounded-md bg-white">
                      {MODES.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label>Référence</Label>
                    <Input value={form.payment_reference} onChange={(e) => set({ payment_reference: e.target.value })} data-testid="retro-payment-reference" placeholder="Réf. virement / Swift" />
                  </div>
                  <div>
                    <Label>Compte bancaire</Label>
                    <select value={form.bank_account} onChange={(e) => set({ bank_account: e.target.value })} data-testid="retro-bank-account" className="w-full h-10 px-2 text-sm border border-zinc-200 rounded-md bg-white">
                      <option value="">—</option>
                      {bankAccounts.map((b, i) => {
                        const label = b.label || b.name || b.bank_name || b.account_label || `Compte ${i + 1}`;
                        return <option key={b.bank_account_id || i} value={label}>{label}</option>;
                      })}
                    </select>
                  </div>
                </div>
                <div>
                  <Label>Commentaire</Label>
                  <Input value={form.payment_comment} onChange={(e) => set({ payment_comment: e.target.value })} data-testid="retro-payment-comment" />
                </div>
              </div>
            )}
            {stagedFileId && (
              <p className="text-[11px] text-zinc-500">Le scan sera joint à la facture (PDF consultable + archivé sur Drive).</p>
            )}
          </div>
          <DialogFooter>
            <button onClick={closeDialog} className="px-3 h-9 border border-zinc-200 text-sm rounded-md hover:bg-zinc-50">Annuler</button>
            {queue.length > 1 && (
              <button
                onClick={skipCurrent}
                data-testid="retro-skip"
                className="px-3 h-9 border border-zinc-200 text-sm text-zinc-600 rounded-md hover:bg-zinc-50"
              >
                Passer {queueIndex + 1 < queue.length ? "→ suivante" : "et terminer"}
              </button>
            )}
            <button
              onClick={submit} disabled={saving || !!dupInfo}
              data-testid="retro-submit"
              className="inline-flex items-center gap-1.5 px-4 h-9 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800 disabled:opacity-50"
            >
              {saving ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />} {editingId ? "Enregistrer les modifications" : "Créer la facture"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RetroInvoiceBlock;
